// How an opponent's turn is dealt out to the board.
//
// A planned turn is several commands — lanes drawn, a policy set, a part
// re-aimed, and one move — and the driver has always issued them one every
// `STEP_MS` so the turn reads as a sequence of things that happened rather than
// as one instant that is already over. That is the right default and it is a
// real cost: a turn that draws five parts is six steps of waiting before the
// player's clock comes back, on every enemy turn, on a board that grows more of
// them.
//
// **"Opponent plays fast" is the opt-out**, and what it relaxes is the
// *ordering*, not the speed. `render/tempo.ts` already plays somebody else's
// turn at up to three times speed; this says the commands do not queue behind
// each other at all — they go out together and their animations overlap.
//
// Pure, and tested apart from the driver for the reason `render/tempo.ts` is:
// it is arithmetic behind a feeling, so a regression looks like the game being
// slightly off rather than like anything failing. It is also the only part of
// the drain that can be tested at all — the rest is timers inside an effect.

/**
 * The shortest gap between one dispatch and the next, whatever the pacing asks.
 *
 * **A floor on the render loop, not on the game.** Nothing here reaches the
 * rules: a beat is when the driver hands an already-decided command to the
 * store, so this changes when the board is *drawn* and never what is on it.
 * The headless rig and the tuner do not go through the driver at all, so a
 * campaign plays exactly as fast as it did.
 *
 * It exists because two settings can ask for no gap at all. "Opponent plays
 * fast" deals a whole turn in one batch, and the watch transport at 8x
 * divides `STEP_MS` down to 32ms — and a board with four camps on it takes
 * four turns between yours, each of which syncs the renderer, re-places every
 * ingot and wakes the on-demand loop. Back to back, the frame never lands.
 *
 * **Applied to every beat, not only the first.** A turn's own commands are
 * plays too — a lane drawn, a part re-aimed — so the rule is "fifty
 * milliseconds after the last dispatch from anybody", and the driver only ever
 * acts after somebody has played, which is what makes the first beat's floor a
 * floor after *their* move.
 *
 * Fifty because it is about three frames: long enough that a sync lands and
 * short enough that a fast turn still reads as fast. It bites only where a
 * setting asked for less — the ordinary 260ms step is untouched, and so is any
 * watch pace up to about five times.
 */
export const MIN_GAP_MS = 50;

/** One firing of the drain: wait this long, then dispatch this many commands. */
export interface TurnBeat {
  /** Milliseconds to wait before it fires, measured from the beat before it. */
  wait: number;
  /** How many commands go out when it does. */
  count: number;
}

export interface TurnDrain {
  beats: TurnBeat[];
  /**
   * How long after the last beat to hand the camera back.
   *
   * Only meaningful when the turn had a move in it — a turn with none never
   * borrowed the view. Kept here rather than left to the driver so the whole
   * shape of a turn is one answer.
   */
  after: number;
}

export interface TurnPace {
  /** The gap between commands. `STEP_MS / pace`. */
  stepMs: number;
  /**
   * How long the camera's lead is, in the same units.
   *
   * The move — and only the move — waits for it. Everything else goes now,
   * which is what plays the free half of the turn *underneath* the pan instead
   * of queueing behind it.
   */
  panMs: number;
  /** Relax the queue entirely: one beat, no waits. */
  fast: boolean;
}

/**
 * The shape of one opponent turn.
 *
 * `moveAt` is the index of the command the camera was sent to, or -1 when the
 * turn has none.
 *
 * **Fast is one beat, not a beat per command with a wait of nought.** The
 * difference is the whole feature: a zero-delay timer per command still spreads
 * them over as many macrotasks, so React renders each one on its own and the
 * board plays them in sequence as fast as the event loop will go — which looks
 * like the same queue running quickly. One beat dispatches them together, so
 * there is a single render, a single diff, and the animations overlap.
 */
export function turnDrain(total: number, moveAt: number, pace: TurnPace): TurnDrain {
  if (total <= 0) return { beats: [], after: 0 };
  // Fast is still one beat carrying the whole turn — what the floor changes is
  // when it fires, not how much it hands over.
  if (pace.fast) return { beats: [{ wait: MIN_GAP_MS, count: total }], after: 0 };

  const beats: TurnBeat[] = [];
  for (let i = 0; i < total; i++) {
    // **Counted, never clocked.** `src/engine/ai/` may not read a clock, and this
    // needs none: every step is the same length, so how much of the pan has
    // already been covered by the time the move comes up is arithmetic on how
    // many commands have gone out before it.
    const covered = (i + 1) * pace.stepMs;
    const owed = i === moveAt ? Math.max(0, pace.panMs - covered) : 0;
    beats.push({ wait: Math.max(MIN_GAP_MS, pace.stepMs + owed), count: 1 });
  }
  // A beat of its own before the hand-back, so the last command is on screen
  // before the camera leaves it.
  return { beats, after: moveAt >= 0 ? pace.stepMs : 0 };
}
